<div class="esims-tabs-container gap-4 mt-2">

  <!-- STATE 1: Loading -->
  <div *ngIf="isLoadingUserEsims" class="loading-esims-container">
    <mat-progress-spinner
      mode="indeterminate"
      color="primary"
      diameter="60">
    </mat-progress-spinner>
    <p class="loading-text">{{ 'ESIM.LOADING_ESIMS' | translate }}</p>
  </div>

  <!-- STATE 2 & 3: Loaded (with or without data) -->
  <ng-container *ngIf="!isLoadingUserEsims">
    <ng-container *ngIf="userEsims.length > 0; else noEsims">

      <!-- My eSIMs Header -->
      <div class="my-esims-header">
        <h2>{{ 'ESIM.MY_ESIM' | translate }}</h2>
        <button mat-raised-button color="primary" (click)="goToEsimSearch()" class="search-esim-btn">
          <mat-icon>search</mat-icon>
          {{ 'ESIM.BTN_SEARCH_ESIM' | translate }}
        </button>
      </div>

      <mat-tab-group
        [(selectedIndex)]="selectedTabIndex"
        class="esims-mat-tabs"
        animationDuration="300ms">

        <!-- Tab 1: Active -->
        <mat-tab>
          <ng-template mat-tab-label>
            <span class="tab-label">
              {{ 'ESIM.TABS.ACTIVE' | translate }}
              <span class="tab-counter">({{ activeCount }})</span>
            </span>
          </ng-template>

          <div class="tab-content">
            <div class="refresh-notice">
              <mat-icon class="refresh-notice__icon">schedule</mat-icon>
              <span>{{ 'ESIM.TABS.ACTIVE_REFRESH_NOTICE' | translate }}</span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
              <ng-container *ngFor="let esim of activeEsims">
                <ng-container *ngTemplateOutlet="esimCard; context: { $implicit: esim }"></ng-container>
              </ng-container>
            </div>

            <div *ngIf="activeEsims.length === 0" class="empty-tab-state">
              <mat-icon class="empty-icon">check_circle_outline</mat-icon>
              <p>{{ 'ESIM.TABS.NO_ACTIVE_ESIMS' | translate }}</p>
            </div>
          </div>
        </mat-tab>

        <!-- Tab 2: Pending/Inactive -->
        <mat-tab>
          <ng-template mat-tab-label>
            <span class="tab-label">
              {{ 'ESIM.TABS.PENDING' | translate }}
              <span class="tab-counter">({{ pendingCount }})</span>
            </span>
          </ng-template>

          <div class="tab-content">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
              <ng-container *ngFor="let esim of pendingEsims">
                <ng-container *ngTemplateOutlet="esimCard; context: { $implicit: esim }"></ng-container>
              </ng-container>
            </div>

            <div *ngIf="pendingEsims.length === 0" class="empty-tab-state">
              <mat-icon class="empty-icon">pending</mat-icon>
              <p>{{ 'ESIM.TABS.NO_PENDING_ESIMS' | translate }}</p>
            </div>
          </div>
        </mat-tab>

        <!-- Tab 3: Other -->
        <mat-tab>
          <ng-template mat-tab-label>
            <span class="tab-label">
              {{ 'ESIM.TABS.OTHER' | translate }}
              <span class="tab-counter">({{ otherCount }})</span>
            </span>
          </ng-template>

          <div class="tab-content">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
              <ng-container *ngFor="let esim of otherEsims">
                <ng-container *ngTemplateOutlet="esimCard; context: { $implicit: esim }"></ng-container>
              </ng-container>
            </div>

            <div *ngIf="otherEsims.length === 0" class="empty-tab-state">
              <mat-icon class="empty-icon">folder_open</mat-icon>
              <p>{{ 'ESIM.TABS.NO_OTHER_ESIMS' | translate }}</p>
            </div>
          </div>
        </mat-tab>

      </mat-tab-group>

    </ng-container>

    <!-- Reusable eSIM Card Template -->
    <ng-template #esimCard let-esim>
      <mat-card class="esim-card lg:max-w-[450px]" appearance="outlined">

        <!-- Status Chip -->
        <div class="status-chip-container">
          <span class="status-chip" [ngClass]="getStatusChipColor(esim.esim_status)">
            {{ getStatusLabel(esim.esim_status) | translate }}
          </span>
        </div>

        <!-- 90-Day Activation Warning (NOT_ACTIVE only) -->
        <div *ngIf="esim.esim_status === 'NOT_ACTIVE' && getDaysUntilRecycling(esim) !== null"
             class="activation-warning-box">
          <mat-icon class="warning-icon">warning</mat-icon>
          <div class="warning-content">
            <p class="warning-title" *ngIf="getDaysUntilRecycling(esim)! <= 0">
              {{ 'ESIM.ACTIVATION_WARNING.URGENT' | translate }}
            </p>
            <p class="warning-title" *ngIf="getDaysUntilRecycling(esim)! > 0">
              {{ 'ESIM.ACTIVATION_WARNING.TITLE' | translate: { days: getDaysUntilRecycling(esim) } }}
            </p>
            <p class="warning-subtitle">
              {{ 'ESIM.ACTIVATION_WARNING.MESSAGE' | translate }}
            </p>
          </div>
        </div>

        <!-- Recycling Explanation (RECYCLED only) -->
        <div *ngIf="esim.esim_status === 'RECYCLED'" class="recycled-info-box">
          <mat-icon class="info-icon">info</mat-icon>
          <p>{{ 'ESIM.RECYCLED_EXPLANATION' | translate }}</p>
        </div>

        <!-- Card Header -->
        <mat-card-header class="mb-2 flex justify-center items-top">
          <div class="absolute left-0 ml-4 p-2 flex" style="height:fit-content;">
            <div *ngFor="let item of esim.pkg_details?.operator_info?.plan_type?.split('-')">
              <img *ngIf="item" width="16" [src]="'./assets/icons/' + item + '_ico.svg'" title="{{ item }}" class="mx-1" />
            </div>
          </div>
          <div class="rounded-full bg-white absolute right-0 mr-4 p-2" style="height:fit-content;">
            <span class="flag large block content-center">
              <img [src]="esim.pkg_details?.country_url || './assets/icons/zendy-card.svg'" />
            </span>
          </div>

          <div class="esim-my-card-image-container relative ml-4">
            <img src="https://cdn-dev.zendy.tel/images/Esim/EsimCard@2x.png" alt="SIM Card" class="esim-my-card-image" />
            <span class="esim-my-country-label">{{ esim.country }}</span>
          </div>
        </mat-card-header>

        <!-- Card Content -->
        <mat-card-content>
          <mat-card-subtitle class="font-bold text-center">{{ esim.details.iccid }}</mat-card-subtitle>
          <mat-card-title class="font-bold text-center">{{ esim.country | titlecase }}</mat-card-title>

          <mat-card-subtitle class="text-center mb-3">
            <span class="font-bold">{{ "ESIM.PURCHASE_DETAILS.VALIDITY" | translate }}:</span>
            {{ esim.validity }}
          </mat-card-subtitle>

          <!-- Status Date -->
          <div *ngIf="getStatusDate(esim)" class="status-date-info text-center mb-3">
            <span class="font-bold">{{ getStatusDateLabel(esim.esim_status) | translate }}:</span>
            {{ formatDate(getStatusDate(esim)) }}
          </div>

          <!-- Consumption Data -->
          <div *ngIf="esim.usage_data?.total && (esim.esim_status === 'ACTIVE' || esim.esim_status === 'NOT_ACTIVE')">
            <strong>{{ 'ESIM.DATA' | translate }}:</strong>
            <p>{{ 'ESIM.USED' | translate }}: {{ esim.usage_data.total - esim.usage_data.remaining }} MB {{ 'ESIM.OF' | translate }} {{ esim.usage_data.total }} MB</p>
            <mat-progress-bar mode="determinate" [value]="calculatePercentage(esim.usage_data.total, esim.usage_data.remaining)"></mat-progress-bar>
          </div>

          <div *ngIf="esim.usage_data?.total_voice && (esim.esim_status === 'ACTIVE' || esim.esim_status === 'NOT_ACTIVE')">
            <strong>{{ 'ESIM.VOICE' | translate }}:</strong>
            <p>{{ 'ESIM.USED' | translate }}: {{ esim.usage_data.total_voice - esim.usage_data.remaining_voice }} min {{ 'ESIM.OF' | translate }} {{ esim.usage_data.total_voice }} min</p>
            <mat-progress-bar mode="determinate" [value]="calculatePercentage(esim.usage_data.total_voice, esim.usage_data.remaining_voice)"></mat-progress-bar>
          </div>

          <div *ngIf="esim.usage_data?.total_text && (esim.esim_status === 'ACTIVE' || esim.esim_status === 'NOT_ACTIVE')">
            <strong>SMS:</strong>
            <p>{{ 'ESIM.USED' | translate }}: {{ esim.usage_data.total_text - esim.usage_data.remaining_text }} {{ 'ESIM.OF' | translate }} {{ esim.usage_data.total_text }}</p>
            <mat-progress-bar mode="determinate" [value]="calculatePercentage(esim.usage_data.total_text, esim.usage_data.remaining_text)"></mat-progress-bar>
          </div>

        </mat-card-content>

        <!-- Card Actions -->
        <mat-card-actions class="buybutton mt-auto bottom-0 left-0">
          <div class="mr-4">
            <button mat-button class="secondary-btn"
                    *ngIf="esim.pkg_details?.operator_info?.rechargeability && esim.esim_status === 'ACTIVE'"
                    (click)="openTopupsDialog(esim.details?.iccid)">
              {{ 'ESIM.TOPUP' | translate }}
            </button>
          </div>
          <div>
            <button mat-button class="view-btn shadow-xl" (click)="openEsimDetailsPage(esim)">
              {{ 'ESIM.BTN_DETAILS' | translate }}
            </button>
          </div>
        </mat-card-actions>

      </mat-card>
    </ng-template>

    <!-- STATE 3: No data -->
    <ng-template #noEsims>
      <div class="no-esims-container text-center">
        <div class="icon-container mb-4">
          <img src="./assets/icons/no-esims-icon.png" alt="No eSIMs" class="no-esims-icon" />
        </div>
        <h2 class="no-esims-title">{{ 'ESIM.NO_SIMS_REGISTERED' | translate }}</h2>
        <p class="no-esims-subtitle">
          {{ 'ESIM.NO_SIMS_DESCRIPTION' | translate }}
        </p>
        <button mat-raised-button color="primary" (click)="goToEsimSearch()" class="search-esim-btn mt-4">
          <mat-icon>search</mat-icon>
          {{ 'ESIM.BTN_SEARCH_ESIM' | translate }}
        </button>
      </div>
    </ng-template>
  </ng-container>

</div>
